// garmentSlot — WHICH garment the character is wearing. One module singleton,
// the single entry point every route writes to (ENG-1108).
//
// Three things can dress the character and they must not each grow their own
// copy of "load a garment onto the avatar":
//
//   - the content browser's card menu (Apply to character),
//   - the `loadGarment` blueprint node,
//   - the `?garment=` deep link.
//
// They all call `wearGarment` / `removeGarment` here; `AvatarModel` subscribes
// and is the ONE place that touches three.js. That is the same split
// `applyEnvironment` + `liveTuneStore` already use for environments, and it is
// what lets the node, the menu and the URL agree by construction rather than by
// three implementations happening to match.
//
// Not React state: the avatar reads it outside the React tree, and the
// content browser lives in a different subtree from the canvas.
//
// `source` says WHO asked, and it decides teardown, exactly as it does for an
// environment:
//   'user'      — a person picked it. Survives blueprint edits; only a person
//                 (or a character switch) takes it off.
//   'blueprint' — a `loadGarment` node. Removed again when no such node is
//                 reachable any more, so deleting the node undresses the
//                 character instead of leaving a garment nothing asks for.
//   'url'       — a `?garment=` link. Like 'blueprint' it does not persist, but
//                 node teardown must not touch it: a link needs no node.
//
// Pure of three.js and of the network, so `node --test` drives it.

/** @typedef {{assetId: string|null, name: string, url: string|null,
 *             source: 'user'|'blueprint'|'url', options: object}} WornGarment */

const EMPTY = Object.freeze({ worn: null })

let _state = EMPTY
const _subs = new Set()

export function garmentSlotState() { return _state }

/** The garment worn right now, or null. */
export function wornGarment() { return _state.worn }

export function subscribeGarmentSlot(fn) {
  _subs.add(fn)
  return () => _subs.delete(fn)
}

function emit() {
  for (const fn of Array.from(_subs)) {
    try { fn(_state) } catch (e) { console.error('[garmentSlot] subscriber threw', e) }
  }
}

function setState(next) {
  _state = Object.freeze(next)
  publish()
  emit()
}

/**
 * Is `next` the same garment as what is worn? Compared on the ASSET ID when
 * both have one, and on the URL otherwise.
 *
 * It matters because a blueprint's `onConstruct` re-fires on every applied
 * editor edit, and re-wearing means re-downloading the mesh, re-binding every
 * vertex and re-culling the body — visible as a flicker, for no change.
 */
export function isSameGarment(a, b) {
  if (!a || !b) return false
  if (a.assetId && b.assetId) return String(a.assetId) === String(b.assetId)
  if (a.url && b.url) return String(a.url).split('?')[0] === String(b.url).split('?')[0]
  return false
}

/**
 * Wear a garment.
 *
 * @param {{assetId?: string|null, name?: string, url?: string|null,
 *          options?: object}} garment
 * @param {{source?: 'user'|'blueprint'|'url'}} [opts]
 * @returns {WornGarment|null} what is now worn, or null when nothing changed.
 */
export function wearGarment(garment, { source = 'user' } = {}) {
  if (!garment) return null
  const next = {
    assetId: garment.assetId ?? null,
    name: garment.name ?? '',
    url: garment.url ?? null,
    options: garment.options ?? {},
    source,
  }
  if (!next.assetId && !next.url) {
    console.warn('[garmentSlot] refusing to wear a garment with neither an asset id nor a URL')
    return null
  }
  // Re-asking for the garment already on is a no-op, BUT a stronger source
  // still takes ownership: a person picking the garment a blueprint loaded
  // makes it theirs, so a later node teardown cannot undress them.
  if (isSameGarment(_state.worn, next)) {
    if (_state.worn.source !== source && source === 'user') {
      setState({ worn: { ..._state.worn, source: 'user' } })
      return _state.worn
    }
    return null
  }
  setState({ worn: next })
  return next
}

/**
 * Take the garment off.
 *
 * `onlySource` is the teardown guard: `removeGarment({ onlySource: 'blueprint' })`
 * removes a garment a NODE loaded and refuses to touch one a person picked.
 * Without it, deleting a `loadGarment` node would undress a character somebody
 * dressed by hand — the mistake `unloadAuthoredEnvironment` exists to prevent
 * on the environment side.
 *
 * @returns {boolean} whether anything was removed.
 */
export function removeGarment({ onlySource = null } = {}) {
  if (!_state.worn) return false
  if (onlySource && _state.worn.source !== onlySource) return false
  setState({ worn: null })
  return true
}

/** Drop everything — a character switch. The next character wears nothing. */
export function resetGarmentSlot() {
  if (!_state.worn) return
  setState({ worn: null })
}

function publish() {
  if (typeof window === 'undefined') return
  window.__GARMENT_SLOT__ = {
    get state() { return _state },
    get worn() { return _state.worn },
    wear: wearGarment,
    remove: removeGarment,
    reset: resetGarmentSlot,
    subscribe: subscribeGarmentSlot,
  }
}

publish()
